/**
 * One row per strategy per rung, for the Research screen's comparison table.
 *
 * **Why this is a module and not a `useMemo`.** The reduction below decides which of a strategy's
 * runs is *the* result, and that choice is the whole table's meaning. It was inline, where the only
 * way to check it was to render thirteen fixture rows and read them — and the fixture that did the
 * rendering pinned three of the nine columns to constants, so five of them were unreviewable. A
 * pure function can be asked directly.
 */
import type { RunResponse, StrategyResponse } from '../api/generated';

export type Metrics = Record<string, string | number | boolean | null>;

/** A metric as a string, with absence preserved rather than coerced to zero. */
export function metric(metrics: Metrics | null | undefined, key: string): string | null {
  const value = metrics?.[key];
  return value === null || value === undefined ? null : String(value);
}

export type Direction = 'both' | 'long' | 'short' | 'untagged';

/**
 * What a strategy's tags say about which way it can trade.
 *
 * 🔴 **Derived, never hand-set.** `ops/seed_strategies.py` writes `long`/`short` from an AST parse
 * of the source (`opens_long` / `opens_short`) because `fleet_invariants` reads `short` to decide
 * which deployments may hold one — a wrong tag *hides* the phantom short (F-D84) that invariant
 * exists to catch. So this reads the tag rather than guessing from a name.
 *
 * ⚠ **Both tags is the interesting case, and it is why "long" is not the absence of "short".** D73
 * needs the market-neutral case to be sayable, so both halves are written. A family carrying both
 * decides its own direction — which is what the eight bidirectional families added 2026-08-24 do.
 *
 * ⚠ **`untagged` is a real state, not a bug.** Tags are written by `seed_strategies`; a strategy
 * registered through the API and never seeded carries none. Rendering it as "long only" would be a
 * claim the data does not make.
 */
export function direction(strategy: StrategyResponse | undefined): Direction {
  const tags = new Set(strategy?.tags ?? []);
  if (tags.has('long') && tags.has('short')) return 'both';
  if (tags.has('short')) return 'short';
  if (tags.has('long')) return 'long';
  return 'untagged';
}

export const DIRECTION_LABEL: Record<Direction, string> = {
  both: 'both ways',
  long: 'long only',
  short: 'short only',
  untagged: 'untagged',
};

export interface StrategyRow {
  id: number;
  name: string;
  way: Direction;
  rung: string;
  symbols: number;
  trades: number;
  ret: number;
  retRaw: string | null;
  sharpe: number;
  sharpeRaw: string | null;
  dd: string | null;
  top5: number;
  top5Raw: string | null;
}

/**
 * The newest completed `full` run for each strategy at each rung.
 *
 * 🔴 **Keyed on strategy *and* rung, because a rung is not a detail.** The same family over the
 * same window at 1h and at 4h is two different results, and D79 pins a verdict to the rung for
 * exactly that reason. Keying on strategy alone would silently drop one and leave a table that
 * looks complete.
 *
 * ⚠ **Only `full` runs.** A walk-forward's headline number answers a different question — it is
 * out-of-sample and averaged over folds — so mixing the two into one "Return" column would compare
 * numbers that are not comparable. The walk-forward has the report below to itself.
 *
 * ⚠ **Newest by id, not by `finished_at`.** A re-run supersedes its predecessor, and `finished_at`
 * is null while a run is still going; id is monotonic and always present.
 *
 * ⚠ **A missing metric stays missing.** `ret`/`sharpe`/`top5` fall back to 0 *for sorting only*,
 * while the `*Raw` fields keep the null so the cell can render an em dash. A run that stored no
 * metrics must not sort as if it broke even.
 */
export function strategyRows(
  runs: readonly RunResponse[],
  strategies: readonly StrategyResponse[],
): StrategyRow[] {
  const byId = new Map(strategies.map((item) => [item.id, item]));
  const best = new Map<string, RunResponse>();
  for (const item of runs) {
    if (item.state !== 'done' || item.kind !== 'full') continue;
    const key = `${item.strategy_id}|${item.timeframe}`;
    const held = best.get(key);
    if (held === undefined || item.id > held.id) best.set(key, item);
  }
  return [...best.values()].map((item) => {
    const strategy = byId.get(item.strategy_id);
    const metrics = (item.metrics ?? null) as Metrics | null;
    return {
      id: item.id,
      name: strategy?.name ?? `strategy ${item.strategy_id}`,
      way: direction(strategy),
      rung: item.timeframe,
      symbols: item.symbols.length,
      trades: Number(metric(metrics, 'trade_count') ?? '0'),
      ret: Number(metric(metrics, 'total_return_pct') ?? '0'),
      retRaw: metric(metrics, 'total_return_pct'),
      sharpe: Number(metric(metrics, 'sharpe') ?? '0'),
      sharpeRaw: metric(metrics, 'sharpe'),
      dd: metric(metrics, 'max_drawdown_pct'),
      top5: Number(metric(metrics, 'top_five_profit_share_pct') ?? '0'),
      top5Raw: metric(metrics, 'top_five_profit_share_pct'),
    };
  });
}

/** How many rows sit in each direction, for the filter's counts. */
export function directionCounts(rows: readonly StrategyRow[]): Map<Direction, number> {
  const seen = new Map<Direction, number>();
  for (const row of rows) seen.set(row.way, (seen.get(row.way) ?? 0) + 1);
  return seen;
}

/**
 * Filter by direction, then sort.
 *
 * ⚠ **Numbers compare numerically, everything else by locale.** Sorting `'−14.39'` and `'2.27'` as
 * strings puts the loss first ascending *and* descending, which is why the numeric twin of each
 * displayed column exists.
 */
export function sortRows(
  rows: readonly StrategyRow[],
  way: string,
  sortKey: string,
  sortDir: 'asc' | 'desc',
): StrategyRow[] {
  const dir = sortDir === 'asc' ? 1 : -1;
  return (
    rows
      .filter((row) => way === '' || row.way === way)
      // ⚠ **Filter first, and the `.slice()` is belt-and-braces.** `filter` already returns a fresh
      // array, so removing `.slice()` changes nothing *today* — but `.sort()` is in-place, and
      // reordering these two lines would sort the caller's rows under it. The no-mutation test
      // catches that reordering, which is the failure worth guarding, not this line.
      .slice()
      .sort((a, b) => {
        const left = a[sortKey as keyof StrategyRow];
        const right = b[sortKey as keyof StrategyRow];
        if (typeof left === 'number' && typeof right === 'number') return (left - right) * dir;
        return String(left).localeCompare(String(right)) * dir;
      })
  );
}
